// src/pages/InquiryList.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  GridComponent, ColumnsDirective, ColumnDirective,
  Resize, Sort, ContextMenu, Filter, Page, ExcelExport, PdfExport, Inject
} from '@syncfusion/ej2-react-grids';
import { Header } from '../Components';
import axios from 'axios';
import {
  FaSearch, FaFileExcel, FaFilePdf, FaEye, FaTimes, FaCopy
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useBaseUrl } from '../Contexts/BaseUrlContext';

const inquiryGrid = [
  {
    field: 'client_id',
    headerText: 'Client Id.',
    width: 120,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Client Id.</span>,
    template: (props) => (
      <div
        className="text-center cursor-pointer text-blue-500 hover:underline"
        onClick={() => props.viewHandler(props.client_id)}
      >
        {props.client_id}
      </div>
    )
  },
  {
    field: 'client_name',
    headerText: 'Client',
    width: 180,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Client</span>,
  },
  {
    field: 'phone_num',
    headerText: 'Contact',
    width: 140,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Contact</span>,
    template: (props) => <div className="truncate">{props.phone_num}{props.phone_num2 ? `, ${props.phone_num2}` : ''}</div>
  },
  {
    field: 'address',
    headerText: 'Address',
    width: 180,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Address</span>,
  },
  {
    field: 'loan_type',
    headerText: 'Loan Type',
    width: 160,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Loan Type</span>,
  },
  {
    field: 'loan_status',
    headerText: 'Status',
    width: 120,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Status</span>,
  },
  {
    field: 'bank_finance',
    headerText: 'Bank/Finance',
    width: 160,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Bank/Finance</span>,
  },
  {
    field: 'reference_by',
    headerText: 'Reference By',
    width: 180,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Reference By</span>,
  },
  {
    field: 'Actions',
    headerText: 'Actions',
    width: 160,
    textAlign: 'Center',
    headerTemplate: () => <span className="font-bold">Actions</span>,
    template: (props) => (
      <div className="flex flex-col items-center gap-2">
        <button
          className="px-3 py-1 bg-green-600 text-white rounded hover:bg-green-700 text-sm w-24 disabled:opacity-60"
          onClick={() => props.sanctionHandler(props.client_id)}
          title="Sanction"
          disabled={props._isLoading}
        >
          Sanction
        </button>

        <button
          className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm w-24 disabled:opacity-60"
          onClick={() => props.rejectHandler(props.client_id)}
          title="Reject"
          disabled={props._isLoading}
        >
          Reject
        </button>
      </div>
    ),
  },
];

const InquiryList = () => {
  const [inquiryData, setInquiryData] = useState([]);
  const [searchTerm, setSearchTerm] = useState(''); // quick search (name, phone, id, loan type)
  const [nameOrId, setNameOrId] = useState(''); // additional name/id specific filter
  const [startDate, setStartDate] = useState(''); // yyyy-mm-dd
  const [endDate, setEndDate] = useState('');
  const [monthFilter, setMonthFilter] = useState(''); // 1-12
  const [yearFilter, setYearFilter] = useState(''); // yyyy

  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const navigate = useNavigate();
  const { baseUrl } = useBaseUrl();

  // Sanction modal state
  const [sanctionModalOpen, setSanctionModalOpen] = useState(false);
  const [sanctionClient, setSanctionClient] = useState(null); // client object
  const [sanctionAmount, setSanctionAmount] = useState('');
  const [sanctionConfirm, setSanctionConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false); // for sanction/reject actions

  // ---------- fetch ----------
  useEffect(() => {
    const fetchInquiries = async () => {
      setIsLoading(true);
      setErrorMessage('');
      const token = localStorage.getItem('authToken');
      if (!token) {
        setErrorMessage('Please log in to access this page.');
        navigate('/');
        setIsLoading(false);
        return;
      }

      try {
        const formData = new FormData();
        formData.append('action', 'loan_list');
        formData.append('type', 'Inquiry');

        const response = await axios.post(
          `${baseUrl}bank-system/loan.php`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
              Authorization: `Bearer ${token}`,
            },
            withCredentials: true,
          }
        );

        console.log('Inquiry List Response:', response.data);

        if (response.data && response.data.status === true && Array.isArray(response.data.data)) {
          setInquiryData(response.data.data);
        } else {
          setInquiryData([]);
          setErrorMessage(response.data?.message || 'Failed to fetch inquiry list.');
        }
      } catch (error) {
        console.error('Axios Error fetching inquiries:', error.response || error);
        setErrorMessage(error.response?.data?.message || 'Error fetching inquiry list.');
        if (error.response?.status === 401) {
          navigate('/');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchInquiries();
  }, [navigate, baseUrl]);

  // ---------- helpers ----------
  const fmt = (n) => {
    try {
      const num = typeof n === 'string' ? parseFloat(n) : n;
      if (Number.isNaN(num)) return n || '—';
      return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(num);
    } catch {
      return n;
    }
  };

  // try to extract a Date object from common date fields
  const getItemDate = (item) => {
    const candidates = ['last_update', 'disbursed_date', 'created_at', 'applied_on', 'applied_at'];
    for (const k of candidates) {
      if (item[k] && item[k] !== '0000-00-00' && item[k] !== '0000-00-00 00:00:00') {
        const s = ('' + item[k]).trim();
        if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return new Date(s + 'T00:00:00');
        const parsed = new Date(s);
        if (!isNaN(parsed.getTime())) return parsed;
      }
    }
    return null;
  };

  // ---------- actions (sanction/reject) - unchanged except we preserve behavior ----------
  const viewHandler = (client_id) => {
    if (!client_id) {
      setErrorMessage('Invalid client id.');
      return;
    }
    navigate(`/user/${client_id}`);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      alert('Copied to clipboard!');
    }).catch((err) => {
      console.error('Failed to copy:', err);
      alert('Failed to copy.');
    });
  };

  const sanctionHandler = (client_id) => {
    const item = inquiryData.find(i => i.client_id === client_id);
    if (!item) {
      setErrorMessage('Client not found.');
      return;
    }
    setSanctionClient(item);
    setSanctionAmount(item.sanction_loan || '');
    setSanctionConfirm(false);
    setSanctionModalOpen(true);
  };

  const submitSanction = async () => {
    if (!sanctionClient) return;
    if (!sanctionConfirm) {
      setErrorMessage('Please confirm sanction by checking the box.');
      return;
    }
    const amount = sanctionAmount?.toString().trim();
    if (!amount || isNaN(Number(amount)) || Number(amount) <= 0) {
      setErrorMessage('Please enter a valid sanction amount.');
      return;
    }

    setActionLoading(true);
    setErrorMessage('');
    const token = localStorage.getItem('authToken');
    if (!token) {
      setErrorMessage('Please log in to perform this action.');
      setActionLoading(false);
      navigate('/');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('action', 'loan_status');
      formData.append('client_id', sanctionClient.client_id);
      formData.append('loan_status', 'Sanction File');
      formData.append('sanction_amount', amount);

      const resp = await axios.post(
        `${baseUrl}bank-system/loan.php`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      console.log('Sanction submit response:', resp.data);

      if (resp.data && resp.data.status === true) {
        alert(resp.data.message || 'Loan status updated successfully.');
        setInquiryData((prev) => prev.map(it =>
          it.client_id === sanctionClient.client_id
            ? { ...it, loan_status: 'Sanction File', sanction_loan: amount }
            : it
        ));
        setSanctionModalOpen(false);
        setSanctionClient(null);
        setSanctionAmount('');
        setSanctionConfirm(false);
        navigate('/sanction-file');
      } else {
        setErrorMessage(resp.data?.message || 'Failed to update loan status.');
      }
    } catch (err) {
      console.error('Sanction error:', err.response || err);
      setErrorMessage(err.response?.data?.message || 'Error performing sanction.');
      if (err.response?.status === 401) navigate('/');
    } finally {
      setActionLoading(false);
    }
  };

  const rejectHandler = async (client_id) => {
    if (!window.confirm(`Are you sure you want to reject Client ID ${client_id}?`)) return;

    setActionLoading(true);
    setErrorMessage('');
    const token = localStorage.getItem('authToken');
    if (!token) {
      setErrorMessage('Please log in to perform this action.');
      setActionLoading(false);
      navigate('/');
      return;
    }

    try {
      const formData = new FormData();
      formData.append('action', 'loan_status');
      formData.append('client_id', client_id);
      formData.append('loan_status', 'Reject File');

      const resp = await axios.post(
        `${baseUrl}bank-system/loan.php`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`,
          },
          withCredentials: true,
        }
      );

      console.log('Reject response:', resp.data);

      if (resp.data && resp.data.status === true) {
        alert(resp.data.message || 'Loan status updated successfully.');
        // Update local UI (mark rejected)
        setInquiryData((prev) => prev.map(it =>
          it.client_id === client_id
            ? { ...it, loan_status: 'Reject File' }
            : it
        ));
        if (selectedInquiry && selectedInquiry.client_id === client_id) {
          setSelectedInquiry(prev => prev ? { ...prev, loan_status: 'Reject File' } : prev);
        }

        // prepare a date to send so the RejectList can pre-filter (prefer last_update)
        const item = inquiryData.find(it => it.client_id === client_id);
        let dateToSend = '';
        if (item) {
          dateToSend = item.last_update || item.disbursed_date || item.created_at || '';
          const m = ('' + dateToSend).match(/^(\d{4}-\d{2}-\d{2})/);
          if (m) dateToSend = m[1];
        }
        if (!dateToSend) dateToSend = new Date().toISOString().slice(0, 10);

        // navigate to reject page with filter params
        navigate(`/reject-file?filterField=last_update&filterValue=${encodeURIComponent(dateToSend)}`);
      } else {
        setErrorMessage(resp.data?.message || 'Failed to update loan status.');
      }
    } catch (err) {
      console.error('Reject error:', err.response || err);
      setErrorMessage(err.response?.data?.message || 'Error performing reject.');
      if (err.response?.status === 401) navigate('/');
    } finally {
      setActionLoading(false);
    }
  };

  // ---------- FILTERING: quick search + name/id + date range + month + year ----------
  const filteredData = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    const nameQ = nameOrId.trim().toLowerCase();
    const start = startDate ? new Date(startDate + 'T00:00:00') : null;
    const end = endDate ? new Date(endDate + 'T23:59:59') : null;
    const month = monthFilter ? Number(monthFilter) : null; // 1-12
    const year = yearFilter ? Number(yearFilter) : null;

    return inquiryData.filter((item) => {
      // quick search across fields
      if (q) {
        const inQuick = (
          (item.client_name || '').toLowerCase().includes(q) ||
          (item.client_id || '').toLowerCase().includes(q) ||
          (item.phone_num || '').toLowerCase().includes(q) ||
          (item.loan_type || '').toLowerCase().includes(q)
        );
        if (!inQuick) return false;
      }

      // name/id specific filter
      if (nameQ) {
        if (!(
          (item.client_name || '').toLowerCase().includes(nameQ) ||
          (item.client_id || '').toLowerCase().includes(nameQ)
        )) return false;
      }

      // date filters: compare item date
      const d = getItemDate(item);
      if (start && d && d < start) return false;
      if (end && d && d > end) return false;

      if (month) {
        if (!d) return false;
        if (d.getMonth() + 1 !== month) return false;
      }

      if (year) {
        if (!d) return false;
        if (d.getFullYear() !== year) return false;
      }

      return true;
    });
  }, [inquiryData, searchTerm, nameOrId, startDate, endDate, monthFilter, yearFilter]);

  // inject handlers to grid rows
  const gridData = filteredData.map((item) => ({
    ...item,
    viewHandler,
    sanctionHandler,
    rejectHandler,
    _isLoading: actionLoading || isLoading,
  }));

  // clear filters helper
  const clearFilters = () => {
    setSearchTerm('');
    setNameOrId('');
    setStartDate('');
    setEndDate('');
    setMonthFilter('');
    setYearFilter('');
  };

  // generate year options from data (fallback range)
  const yearOptions = useMemo(() => {
    const years = new Set();
    inquiryData.forEach(item => {
      const d = getItemDate(item);
      if (d) years.add(d.getFullYear());
    });
    if (years.size === 0) {
      return [2020, 2021, 2022, 2023, 2024, 2025, 2026];
    }
    return Array.from(years).sort((a, b) => b - a);
  }, [inquiryData]);

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 lg:p-8 animate-fadeIn min-h-screen">
      <Header category="Dashboard" title="Inquiry" />

      {/* Error Message */}
      {errorMessage && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-lg flex items-center">
          <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
          {errorMessage}
        </div>
      )}

      {/* First row: Quick search + exports */}
      <div className="mb-4 flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Quick search (name, phone, id, loan type)..."
            className="w-full pl-10 pr-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300 bg-white shadow-sm"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          <button
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() => {
              const grid = document.getElementById('inquiryGrid')?.ej2_instances?.[0];
              if (grid) grid.excelExport({ fileName: `inquiries_${startDate || 'all'}_${endDate || ''}.xlsx` });
            }}
            disabled={isLoading}
          >
            <FaFileExcel className="mr-2" /> Export Excel
          </button>
          <button
            className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition duration-300 shadow-md disabled:opacity-50"
            onClick={() => {
              const grid = document.getElementById('inquiryGrid')?.ej2_instances?.[0];
              if (grid) grid.pdfExport({
                fileName: `inquiries_${startDate || 'all'}_${endDate || ''}.pdf`,
                title: `Inquiries ${startDate || ''}${endDate ? ' - ' + endDate : ''}`
              });
            }}
            disabled={isLoading}
          >
            <FaFilePdf className="mr-2" /> Export PDF
          </button>
        </div>
      </div>

    {/* FILTER ROW (Separate clean row) */}
      <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4 p-4 rounded-lg">
        {/* Start Date */}
        <div>
          <label className="text-sm text-gray-600">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full mt-1 p-2 border rounded"
          />
        </div>

        {/* End Date */}
        <div>
          <label className="text-sm text-gray-600">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full mt-1 p-2 border rounded"
          />
        </div>

        {/* Month */}
        <div>
          <label className="text-sm text-gray-600">Month</label>
          <select
            value={monthFilter}
            onChange={(e) => setMonthFilter(e.target.value)}
            className="w-full mt-1 p-2 border rounded"
          >
            <option value="">All Months</option>
            {Array.from({ length: 12 }).map((_, i) => (
              <option key={i} value={i + 1}>
                {new Date(0, i).toLocaleString('default', { month: 'long' })}
              </option>
            ))}
          </select>
        </div>

        {/* Year */}
        <div>
          <label className="text-sm text-gray-600">Year</label>
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="w-full mt-1 p-2 border rounded"
          >
            <option value="">All Years</option>
            {/* simple range; adjust if you want dynamic years from data */}
            {Array.from({ length: 6 }).map((_, i) => {
              const y = 2022 + i;
              return <option key={y} value={y}>{y}</option>;
            })}
          </select>
        </div>

        {/* Clear */}
        <div className="flex items-end">
          <button
            onClick={() => {
              setStartDate(''); setEndDate(''); setMonthFilter(''); setYearFilter('');
            }}
            className="w-full bg-gray-600 text-white py-2 rounded hover:bg-gray-700"
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Loading */}
      {(isLoading || actionLoading) && (
        <div className="flex justify-center items-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-600"></div>
        </div>
      )}

      {/* Grid */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden">
        <GridComponent
          id="inquiryGrid"
          dataSource={gridData}
          allowPaging={true}
          allowSorting={true}
          allowExcelExport={true}
          allowPdfExport={true}
          allowFiltering={true}
          allowResizing={true}
          pageSettings={{ pageSize: 10, pageSizes: [10, 20, 50] }}
          filterSettings={{ type: 'Menu', showFilterBarStatus: true }}
        >
          <ColumnsDirective>
            {inquiryGrid.map((col, idx) => (
              <ColumnDirective key={idx} {...col} />
            ))}
          </ColumnsDirective>
          <Inject services={[Resize, Sort, ContextMenu, Filter, Page, ExcelExport, PdfExport]} />
        </GridComponent>
      </div>

      {/* Modal for Viewing Inquiry Details */}
      {selectedInquiry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl flex flex-col">
            <div className="flex justify-between items-center mb-4 sticky top-0 bg-white z-10 border-b border-gray-200 pb-2">
              <h2 className="text-xl font-bold text-gray-900">Client Details</h2>
              <button
                className="text-gray-500 hover:text-gray-700 transition-colors"
                onClick={() => setSelectedInquiry(null)}
              >
                <FaTimes size={22} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto space-y-4 text-gray-700">
              <div className="grid grid-cols-2 gap-4">
                <div><strong>Client Id:</strong></div>
                <div className="text-blue-600">{selectedInquiry.client_id}</div>

                <div><strong>Client UID:</strong></div>
                <div>{selectedInquiry.client_uid || '-'}</div>

                <div><strong>Client Name:</strong></div>
                <div>{selectedInquiry.client_name || '-'}</div>

                <div><strong>Contact 1:</strong></div>
                <div>{selectedInquiry.phone_num || '-'}</div>

                <div><strong>Contact 2:</strong></div>
                <div>{selectedInquiry.phone_num2 || '-'}</div>

                <div><strong>Address:</strong></div>
                <div>{selectedInquiry.address || '-'}</div>

                <div><strong>Loan Type:</strong></div>
                <div>{selectedInquiry.loan_type || '-'}</div>

                <div><strong>Loan Status:</strong></div>
                <div>{selectedInquiry.loan_status || '-'}</div>

                <div><strong>Required Loan:</strong></div>
                <div>{selectedInquiry.required_loan || '-'}</div>

                <div><strong>Sanction Loan:</strong></div>
                <div>{selectedInquiry.sanction_loan || '-'}</div>

                <div><strong>Bank / Finance:</strong></div>
                <div>{selectedInquiry.bank_finance || '-'}</div>

                <div><strong>Reference By:</strong></div>
                <div>{selectedInquiry.reference_by || '-'}</div>

                <div><strong>Disbursed Date:</strong></div>
                <div>{selectedInquiry.disbursed_date && selectedInquiry.disbursed_date !== '0000-00-00' ? selectedInquiry.disbursed_date : '-'}</div>

                <div><strong>Remark:</strong></div>
                <div className="col-span-1">{selectedInquiry.remark || '-'}</div>
              </div>

              <div className="mt-3">
                <button
                  className="px-3 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 mr-2"
                  onClick={() => {
                    copyToClipboard(JSON.stringify(selectedInquiry, null, 2));
                    alert('Client JSON copied to clipboard');
                  }}
                >
                  Copy JSON
                </button>
                <button
                  className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                  onClick={() => {
                    navigate(`/user/${selectedInquiry.client_id}`);
                    setSelectedInquiry(null);
                  }}
                >
                  Open User Page
                </button>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2 sticky bottom-0 bg-white border-t border-gray-200 pt-4">
              <button
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                onClick={() => setSelectedInquiry(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Sanction Modal */}
      {sanctionModalOpen && sanctionClient && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-2xl">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-semibold">Sanction Amount</h3>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setSanctionModalOpen(false)}><FaTimes /></button>
            </div>

            <div className="space-y-3">
              <div>
                <div className="text-sm text-gray-600">Client ID</div>
                <div className="font-medium">{sanctionClient.client_id} — {sanctionClient.client_name}</div>
              </div>

              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={sanctionConfirm}
                    onChange={(e) => setSanctionConfirm(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm">I confirm sanction for this client</span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Sanction Amount</label>
                <input
                  type="number"
                  min="0"
                  value={sanctionAmount}
                  onChange={(e) => setSanctionAmount(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter sanction amount"
                />
              </div>

              {errorMessage && (
                <div className="p-2 bg-red-100 text-red-700 rounded">{errorMessage}</div>
              )}
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <button
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
                onClick={() => {
                  setSanctionModalOpen(false);
                  setSanctionClient(null);
                  setSanctionAmount('');
                  setSanctionConfirm(false);
                  setErrorMessage('');
                }}
                disabled={actionLoading}
              >
                Cancel
              </button>

              <button
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-60"
                onClick={submitSanction}
                disabled={actionLoading || !sanctionConfirm || !sanctionAmount || Number(sanctionAmount) <= 0}
              >
                {actionLoading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InquiryList;
